import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Mail, 
  Phone, 
  Calendar, 
  Shield, 
  CheckCircle,
  XCircle,
  AlertCircle,
  Tag,
  Edit3,
  Clock,
  Eye,
  Users,
  ChevronRight
} from 'lucide-react';
import moment from 'moment';
import { useCustomRoles } from '../../hooks/permissionHooks/useCustomRoles';
import PermissionsModal from "./PermissionsModal";
const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    switch(status?.toLowerCase()) {
      case 'active':
        return { 
          icon: CheckCircle,
          bg: 'bg-emerald-500', 
          textColor: 'text-white',
          dotColor: 'bg-emerald-400',
          label: 'Active'
        };
      case 'inactive':
        return { 
          icon: XCircle,
          bg: 'bg-red-500', 
          textColor: 'text-white',
          dotColor: 'bg-red-400',
          label: 'Inactive'
        };
      default:
        return { 
          icon: AlertCircle,
          bg: 'bg-amber-500', 
          textColor: 'text-white',
          dotColor: 'bg-amber-400',
          label: 'Unknown'
        };
    }
  };

  const config = getStatusConfig(status);
  const StatusIcon = config.icon;

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${config.bg} ${config.textColor} shadow-lg`}>
      <div className={`w-2 h-2 rounded-full ${config.dotColor} animate-pulse`}></div>
      <StatusIcon className="w-4 h-4" />
      {config.label}
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, value, delay = 0, accent = false, onClick, clickable = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, type: "spring", stiffness: 300 }}
    className={`group relative p-4 rounded-xl border transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
      accent 
        ? 'bg-gradient-to-br from-[#FF6C6C]/5 to-pink-50 border-[#FF6C6C]/20 hover:border-[#FF6C6C]/40' 
        : 'bg-white border-gray-200 hover:border-gray-300'
    } ${clickable ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <div className="flex gap-3 items-start">
      <div className={`p-2 rounded-lg transition-all duration-300 group-hover:scale-110 ${
        accent 
          ? 'bg-gradient-to-br from-[#FF6C6C] to-pink-500 text-white shadow-md shadow-[#FF6C6C]/25' 
          : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center">
          <p className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">{title}</p>
          {clickable && (
            <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-[#FF6C6C] transition-colors" />
          )}
        </div>
        <p className="text-sm font-semibold text-gray-900 truncate">{value || 'N/A'}</p>
        {clickable && (
          <p className="mt-1 text-xs text-gray-500">Click to view details</p>
        )}
      </div>
    </div>
  </motion.div>
);



const ViewTeamMemberModal = ({ isOpen, onClose, teamMember, onEdit }) => {
  const [showPermissions, setShowPermissions] = useState(false);
  const { customRoles } = useCustomRoles();

  if (!isOpen || !teamMember) return null;

  const getUserPermissions = () => {
    if (teamMember.permissions && typeof teamMember.permissions === 'object') {
      return teamMember.permissions;
    }

    if (teamMember.role && customRoles.length > 0) {
      const memberRole = customRoles.find(role => role.name === teamMember.role);
      if (memberRole && memberRole.permissions) {
        return memberRole.permissions;
      }
    }
    return {};
  };

  const userPermissions = getUserPermissions();

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed top-0 right-0 bottom-0 left-0 z-50 justify-center items-center backdrop-blur-md bg-black/60"
          style={{ margin: 0, padding: 0 }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full max-w-4xl max-h-[90vh] mx-4 my-4 overflow-hidden bg-white rounded-3xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Enhanced Header without image placeholder */}
            <div className="overflow-hidden relative flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6C6C] via-pink-500 to-purple-600"></div>
              <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/20"></div>
              <div className="relative p-6">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-full transition-all duration-200 text-white/80 hover:text-white hover:bg-white/20 hover:rotate-90"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="flex flex-col gap-4 items-start sm:flex-row sm:items-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 400 }}
                    className="relative"
                  >
                    <StatusBadge status={teamMember.status} />
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex-1 text-white"
                  >
                    <h1 className="mb-2 text-xl font-bold sm:text-2xl">{teamMember.full_name || teamMember.fullName}</h1>
                    <div className="flex flex-wrap gap-2 items-center">
                      <div className="flex gap-2 items-center px-3 py-1.5 rounded-full backdrop-blur-sm bg-white/20">
                        <Shield className="w-3 h-3" />
                        <span className="text-xs font-medium">{teamMember.role || 'No role assigned'}</span>
                      </div>
                      <div className="flex gap-2 items-center px-3 py-1.5 rounded-full backdrop-blur-sm bg-white/20">
                        <Clock className="w-3 h-3" />
                        <span className="text-xs">Member since {teamMember.created_at ? moment(teamMember.created_at).format('MMM YYYY') : 'N/A'}</span>
                      </div>
                      <div className="flex gap-2 items-center px-3 py-1.5 rounded-full backdrop-blur-sm bg-white/20">
                        <Users className="w-3 h-3" />
                        <span className="text-xs">Team Member</span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Enhanced Content with compact sections */}
            <div className="overflow-y-auto flex-1 p-4">
              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-4"
              >
                <div className="flex gap-2 items-center mb-3">
                  <div className="p-1.5 bg-blue-100 rounded-lg">
                    <Mail className="w-3 h-3 text-blue-600" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">Contact Information</h2>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <InfoCard 
                    icon={Mail} 
                    title="Email Address" 
                    value={teamMember.email}
                    delay={0.5}
                    accent={true}
                  />
                  <InfoCard 
                    icon={Phone} 
                    title="Phone Number" 
                    value={teamMember.phone}
                    delay={0.6}
                  />
                </div>
              </motion.div>

              {/* Role & Permissions */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="mb-4"
              >
                <div className="flex gap-2 items-center mb-3">
                  <div className="p-1.5 bg-purple-100 rounded-lg">
                    <Shield className="w-3 h-3 text-purple-600" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">Role & Access</h2>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <InfoCard 
                    icon={Tag} 
                    title="Assigned Role" 
                    value={teamMember.role}
                    delay={0.8}
                    accent={true}
                  />
                  <InfoCard 
                    icon={Eye} 
                    title="View Permissions" 
                    value="Click to view all permissions"
                    delay={0.9}
                    clickable={true}
                    onClick={() => setShowPermissions(true)}
                  />
                </div>
              </motion.div>

              {/* Account Details */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
                className="mb-4"
              >
                <div className="flex gap-2 items-center mb-3">
                  <div className="p-1.5 bg-green-100 rounded-lg">
                    <Calendar className="w-3 h-3 text-green-600" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">Account Details</h2>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <InfoCard 
                    icon={Calendar} 
                    title="Date Created" 
                    value={teamMember.createdOn ? moment(teamMember.createdOn).format('DD MMM YYYY') : teamMember.createdOn}
                    delay={1.1}
                  />
                  <InfoCard 
                    icon={CheckCircle} 
                    title="Account Status" 
                    value={teamMember.status}
                    delay={1.2}
                    accent={true}
                  />
                </div>
              </motion.div>

              {/* Additional Information */}
              {teamMember.lastLogin && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.3 }}
                  className="mb-4"
                >
                  <div className="flex gap-2 items-center mb-3">
                    <div className="p-1.5 bg-amber-100 rounded-lg">
                      <Clock className="w-3 h-3 text-amber-600" />
                    </div>
                    <h2 className="text-base font-semibold text-gray-900">Activity</h2>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <InfoCard 
                      icon={Clock} 
                      title="Last Login" 
                      value={moment(teamMember.lastLogin).format('DD MMM YYYY, HH:mm')}
                      delay={1.4}
                    />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Enhanced Footer Actions */}
            <div className="flex-shrink-0 p-4 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-col gap-2 justify-end sm:flex-row">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowPermissions(true)}
                  className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg border-2 border-purple-200 transition-all duration-200 hover:bg-purple-100 hover:border-purple-300"
                >
                  <Eye className="w-4 h-4" />
                  View Permissions
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white rounded-lg border-2 border-gray-200 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300"
                >
                  Close
                </motion.button>
                {onEdit && (
                  <motion.button
                    whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(255, 108, 108, 0.4)" }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      onEdit(teamMember);
                      onClose();
                    }}
                    className="px-4 py-2 text-sm text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-lg hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium shadow-lg shadow-[#FF6C6C]/25 flex items-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Member
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Permissions Modal */}
      <PermissionsModal
        isOpen={showPermissions} 
        onClose={() => setShowPermissions(false)} 
        teamMember={teamMember} 
        userPermissions={userPermissions}
      />
    </>
  );
};

export default ViewTeamMemberModal; 